import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getAdReportReasons, reportAd } from "../../hook/useAdsApi";

// Report modal for a sponsored ad (see ads.routes.js: GET .../ads/report-reasons,
// POST .../ads/:adId/reports). Styled after ShareModal.js's bottom-sheet pattern.
export default function ReportAdModal({ isOpen, onClose, adId, onSuccess }) {
  const [reasons, setReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [alreadyReported, setAlreadyReported] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setReason("");
    setComment("");
    setError("");
    setAlreadyReported(false);
    setLoadingReasons(true);
    getAdReportReasons()
      .then((list) => setReasons(Array.isArray(list) ? list : []))
      .catch(() => setError("Failed to load report reasons. Please try again."))
      .finally(() => setLoadingReasons(false));
  }, [isOpen, adId]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) {
      setError("Please select a reason.");
      return;
    }
    if (reason === "OTHER" && !comment.trim()) {
      setError('Please tell us more - a comment is required for "Other".');
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const result = await reportAd(adId, { reason, comment: comment.trim() || undefined });
      toast.success(result?.message || "Thanks for your feedback. We'll review this ad.");
      onSuccess?.();
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      if (status === 409) {
        setAlreadyReported(true);
      } else if (status === 403) {
        setError(message || "You cannot report your own ad.");
      } else {
        setError(message || "Failed to submit report. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-50 flex justify-center items-end md:items-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl p-6 shadow-lg relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-black text-xl font-bold"
          aria-label="Close"
        >
          ✕
        </button>

        <h2 className="text-xl font-bold mb-1 text-[#BE1F2E]">Report Ad</h2>

        {alreadyReported ? (
          <p className="text-sm text-black font-medium mt-4">You have already reported this ad</p>
        ) : (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <p className="text-sm text-gray-600">Help us understand what's wrong with this ad.</p>

            <div>
              <label className="block text-sm font-bold text-black mb-2">Reason</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={loadingReasons || submitting}
                className="w-full h-12 px-3 rounded-xl border border-[#095228] bg-white text-sm focus:outline-none"
              >
                <option value="" disabled>
                  {loadingReasons ? "Loading reasons..." : "Select a reason"}
                </option>
                {reasons.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.display_name}
                  </option>
                ))}
              </select>
            </div>

            {reason === "OTHER" && (
              <div>
                <label className="block text-sm font-bold text-black mb-2">Tell us more</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  className="w-full px-3 py-2 rounded-xl border border-[#095228] text-sm focus:outline-none"
                  placeholder="What's wrong with this ad?"
                />
              </div>
            )}

            {error && <p className="text-red-500 text-xs">{error}</p>}

            <button
              type="submit"
              disabled={submitting || loadingReasons}
              className="w-full bg-[#BE1F2E] text-white h-12 rounded-full font-bold text-sm disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Submit Report"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
